// PersonalInfo.tsx
import React, { useEffect, useMemo, useState } from 'react';
// import axios from "axios";
import axios from "../lib/axios";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from 'react-router-dom';
// import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
// import FullScreenLoader from "@/components/FullScreenLoader";
import { useLoader } from "../contexts/LoaderContext";

// import { Calendar } from "@/components/ui/calendar";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
// import { CalendarIcon } from "lucide-react";
// import { cn } from "@/lib/utils";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { API_BASE_URL } from '../config';
import { CompositePersonalCommunicationAddress, CompositePersonalResidentialAddress } from '@/apps/los/components/CompositeAddress/CompositeAddress';
import CallbackHelp from "@/components/CallbackHelp";
import { useJourney } from "@/contexts/JourneyContext";
import JourneyHeader from "@/components/JourneyHeader";
import {
  stringOrEmpty,
  stringOrEmptyOptional,
  stringMandatory,
  panString,
  dateOfBirthString,
  phoneNumberString,
  pincodeNumberString,
  addressString,
  consentTrue,
} from "@/helpers/validations";
// --------------------
// LOAD SCHEMA
// --------------------
const personalFormLoadSchema = z.object({
  // 1. Name
  individual_name: stringOrEmpty("Name"),

  // 2. Date of Birth
  date_of_birth: stringOrEmpty("Date of Birth"),

  // 3. Gender
  gender: stringOrEmpty("Gender"),

  // 4. Phone Number
  phone_number: stringOrEmpty("Phone Number"),

  // 5. Individual PAN
  individual_pan_number: stringOrEmpty("PAN"),

  communication_address_line1: stringOrEmptyOptional("Communication Address Line 1"),
  communication_address_line2: stringOrEmptyOptional("Communication Address Line 2"),
  communication_address_line3: stringOrEmptyOptional("Communication City / Town"),
  communication_address_landmark: stringOrEmptyOptional("Communication Address Landmark"),
  communication_address_pincode: stringOrEmptyOptional("Communication Address Pincode"),

  residential_address_line1: stringOrEmptyOptional("Residential Address Line 1"),
  residential_address_line2: stringOrEmptyOptional("Residential Address Line 2"),
  residential_address_line3: stringOrEmptyOptional("Residential City / Town"),
  residential_address_landmark: stringOrEmptyOptional("Residential Address Landmark"),
  residential_address_pincode: stringOrEmptyOptional("Residential Address Pincode"),

  // 8. Consents (default to false so checkboxes aren’t null)
  declares_hhi_above_3_lakh: z.boolean().default(false),

  // 9. Enrichment fields (read-only display, not submitted)
  aadhaar_linked: z.boolean().nullable().optional(),
  masked_aadhaar_number: stringOrEmptyOptional("Masked Aadhaar"),
});

// --------------------
// SUBMIT SCHEMA
// --------------------
const personalFormSubmitSchema = z.object({
  // 1. Name
  individual_name: stringMandatory("Name"),

  // 2. Date of Birth (valid date, ≥18 years, not future)
  date_of_birth: dateOfBirthString("Date of Birth"),

  // 3. Gender
  gender: stringMandatory("Gender"),

  // 4. Phone Number (10 digits only)
  phone_number: phoneNumberString("Phone Number"),

  // 5. Individual PAN (ABCDE1234F pattern)
  individual_pan_number: panString("PAN"),

  // 6. Email (optional but must be valid if provided)
  email: z
    .string()
    .trim()
    .refine((v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), {
      message: "Email must be a valid email address",
    })
    .optional()
    .default(""),

  communication_address_line1: stringMandatory("Communication Flat / House no / Building name"),
  communication_address_line2: stringMandatory("Communication Address Line 2"),
  communication_address_line3: stringMandatory("Communication City / Town"),
  communication_address_landmark: stringOrEmptyOptional("Communication Address Landmark"),
  communication_address_pincode: pincodeNumberString("Communication Address Pincode"),

  residential_address_line1: stringMandatory("Residential Flat / House no / Building name"),
  residential_address_line2: stringMandatory("Residential Address Line 2"),
  residential_address_line3: stringMandatory("Residential City / Town"),
  residential_address_landmark: stringOrEmptyOptional("Residential Address Landmark"),
  residential_address_pincode: pincodeNumberString("Residential Address Pincode"),

  // 8. Consents (all required to be true)
  declares_hhi_above_3_lakh: z.boolean(),
})

const PersonalInfo = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [sameAsComm, setSameAsComm] = React.useState(false);
  const { loading, setLoading } = useLoader();
  const { refresh: refreshJourney } = useJourney();
  const [isNameEditable, setIsNameEditable] = useState(false);

  // Enrichment display state (read-only, from API)
  const [aadhaarLinked, setAadhaarLinked] = useState<boolean | null>(null);
  const [maskedAadhaar, setMaskedAadhaar] = useState<string>("");

  type FormValues = z.infer<typeof personalFormSubmitSchema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(personalFormSubmitSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      individual_name: "",
      date_of_birth: "",
      gender: "",
      phone_number: "",
      individual_pan_number: "",

      communication_address_line1: "",
      communication_address_line2: "",
      communication_address_line3: "",
      communication_address_landmark: "",
      communication_address_pincode: "",

      residential_address_line1: "",
      residential_address_line2: "",
      residential_address_line3: "",
      residential_address_landmark: "",
      residential_address_pincode: "",

      declares_hhi_above_3_lakh: false,
    },
  });


  React.useEffect(() => {
    const subscription = form.watch((values) => {
      const comm = {
        line1: values.communication_address_line1?.trim() || "",
        line2: values.communication_address_line2?.trim() || "",
        line3: values.communication_address_line3?.trim() || "",
        landmark: values.communication_address_landmark?.trim() || "",
        pincode: values.communication_address_pincode?.trim() || "",
      };

      const res = {
        line1: values.residential_address_line1?.trim() || "",
        line2: values.residential_address_line2?.trim() || "",
        line3: values.residential_address_line3?.trim() || "",
        landmark: values.residential_address_landmark?.trim() || "",
        pincode: values.residential_address_pincode?.trim() || "",
      };

      const allMatch =
        comm.line1 === res.line1 &&
        comm.line2 === res.line2 &&
        comm.line3 === res.line3 &&
        comm.landmark === res.landmark &&
        comm.pincode === res.pincode &&
        Object.values(comm).some((v) => v !== ""); // avoid matching empty addresses

      setSameAsComm(allMatch);
    });

    return () => subscription.unsubscribe();
  }, [form]);


  const {
    register,
    handleSubmit,
    reset,
    trigger,
    setError,
    getValues,
    formState: { errors },
  } = form;

  // The onSubmit function that you define to process the data after validation
  const onSubmit = (data: FormValues) => {
    setLoading(true);

    console.log("Form data for submission:", data);
    axios
      .post(`${API_BASE_URL}/api/set_individual_details/`, data, { maxRedirects: 0 })
      .then((res) => {
        console.log("Backend response:", res.data);
        console.log("Status:", res.data.status);
        console.log("Message:", res.data.message);

        console.log(res.status);
        console.log(res.statusText);
        if (res.status === 200) {
          // Refresh journey state to get the updated stage after API advances it
          refreshJourney().then(() => {
            // Small delay to ensure state has propagated to JourneySync
            setTimeout(() => {
              if (res.data.next_page_url) {
                navigate(res.data.next_page_url);
              } else {
                navigate("/business-info");
              }
            }, 150);
          }).catch(() => {
            // If refresh fails, navigate anyway
            setTimeout(() => {
              if (res.data.next_page_url) {
                navigate(res.data.next_page_url);
              } else {
                navigate("/business-info");
              }
            }, 150);
          });
        }
        else if (res.request.responseURL.includes("https://apply.intellendtech.com/accounts/login/")) {
          navigate("/session-timeout");
        }
      })
      .catch((err) => {
        if (confirm("Error saving data: " + err.message + ".\n Press OK to reload the page.")) {
          window.location.reload();
        }
      })
      .finally(() => setLoading(false));
  };


  React.useEffect(() => {
    if (sameAsComm) {
      const comm = {
        line1: form.watch("communication_address_line1"),
        line2: form.watch("communication_address_line2"),
        line3: form.watch("communication_address_line3"),
        landmark: form.watch("communication_address_landmark"),
        pincode: form.watch("communication_address_pincode"),
      };

      form.setValue("residential_address_line1", comm.line1 ?? "");
      form.setValue("residential_address_line2", comm.line2 ?? "");
      form.setValue("residential_address_line3", comm.line3 ?? "");
      form.setValue("residential_address_landmark", comm.landmark ?? "");
      form.setValue("residential_address_pincode", comm.pincode ?? "");
    }
  }, [
    sameAsComm,
    form.watch("communication_address_line1"),
    form.watch("communication_address_line2"),
    form.watch("communication_address_line3"),
    form.watch("communication_address_landmark"),
    form.watch("communication_address_pincode"),
  ]);



  // Fetching data from backend
  // and setting it to the form
  useEffect(() => {
    setLoading(true);
    axios
      .post(`${API_BASE_URL}/api/get_individual_details/`, {}, { maxRedirects: 0 })
      .then(async (res) => {
        if (res.request.responseURL.includes("https://apply.intellendtech.com/accounts/login/")) {
          navigate("/session-timeout");
        }
        console.log("Backend response:", res.data);

        type LoadValues = z.infer<typeof personalFormLoadSchema>;
        type SubmitValues = z.infer<typeof personalFormSubmitSchema>;

        if (!res.data.individual_name) {
          setIsNameEditable(true);
        }

        // 1. Validate response against Load Schema
        const loadResult = personalFormLoadSchema.safeParse(res.data);

        if (!loadResult.success) {
          console.error("Load schema validation failed:", loadResult.error);

          // Flatten errors
          const flatErrors = loadResult.error.flatten();
          Object.entries(flatErrors.fieldErrors).forEach(([field, messages]) => {
            if (messages?.length) {
              setError(field as keyof LoadValues, {
                type: "manual",
                message: messages[0],
              });
            }
          });

          return; // stop if data unusable
        }

        // 2. Reset form with clean data
        const parsedLoadData = loadResult.data;
        const resAddr = {
          line1: parsedLoadData.residential_address_line1?.trim() || "",
          line2: parsedLoadData.residential_address_line2?.trim() || "",
          line3: parsedLoadData.residential_address_line3?.trim() || "",
          landmark: parsedLoadData.residential_address_landmark?.trim() || "",
          pincode: parsedLoadData.residential_address_pincode?.trim() || "",
        };

        const comAddr = {
          line1: parsedLoadData.communication_address_line1?.trim() || "",
          line2: parsedLoadData.communication_address_line2?.trim() || "",
          line3: parsedLoadData.communication_address_line3?.trim() || "",
          landmark: parsedLoadData.communication_address_landmark?.trim() || "",
          pincode: parsedLoadData.communication_address_pincode?.trim() || "",
        };

        const areSame =
          resAddr.line1 === comAddr.line1 &&
          resAddr.line2 === comAddr.line2 &&
          resAddr.line3 === comAddr.line3 &&
          resAddr.landmark === comAddr.landmark &&
          resAddr.pincode === comAddr.pincode;

        setSameAsComm(areSame);
        reset(parsedLoadData);

        // ✅ Set enrichment display state (read-only, not part of form)
        setAadhaarLinked(
          typeof res.data.aadhaar_linked === "boolean" ? res.data.aadhaar_linked : null
        );
        setMaskedAadhaar(res.data.masked_aadhaar_number || "");

        // ✅ Wait for reset to finish, then trigger validation
        await new Promise((resolve) => setTimeout(resolve, 0));
        // await trigger();

        // // 3. Now safely check Submit Schema
        // const currentValues = form.getValues();
        // const submitResult = personalFormSubmitSchema.safeParse(currentValues);

        // if (!submitResult.success) {
        //   console.warn("Submit schema validation failed:", submitResult.error);

        //   const flatErrors = submitResult.error.flatten();
        //   Object.entries(flatErrors.fieldErrors).forEach(([field, messages]) => {
        //     if (messages?.length) {
        //       setError(field as keyof SubmitValues, {
        //         type: "manual",
        //         message: messages[0],
        //       });
        //     }
        //   });
        // }

        // // Debug after RHF finishes updating formState
        // // await new Promise((resolve) => setTimeout(resolve, 0));
        // console.log("Form errors after submit schema check:", form.formState.errors);
      })
      .catch((err) => {
        if (confirm("Error saving data: " + err.message + ".\n Press OK to reload the page.")) {
          window.location.reload();
        }
      })
      .finally(() => setLoading(false));




    // const loadResult = personalFormLoadSchema.safeParse(res.data);

    // if (!loadResult.success) {
    //   console.error("Load schema validation failed:", loadResult.error);
    //   return;
    // }

    // const parsed = loadResult.data;

    // figure out "same as comm"

    // reset(parsed);

    // wait for reset, then run resolver once
    // await new Promise((r) => setTimeout(r, 0));
    // await trigger(); // this updates formState.isValid

    // setSameAsComm(areSame);
    // })
    // .catch((err) => console.error("Fetch error:", err))
    // .finally(() => setLoading(false));
  }, []);

  const [startDate, setStartDate] = useState<Date | null>(new Date());


  return (
    <div className="min-h-screen bg-intellend-white">
      {/* Compact header */}
      <JourneyHeader />

      {/* Main content */}
      <main className="container mx-auto px-3 py-4 max-w-2xl">
        <div className="bg-white shadow-md rounded-lg p-4 mb-4">
          <h2 className="text-xl font-bold text-intellend-green mb-4">Personal Information</h2>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <fieldset className="border border-gray-300 rounded p-4 space-y-4">
                <legend className="font-bold">Applicant's Details</legend>
                {/* 1. Name */}
                <FormField
                  control={form.control}
                  name="individual_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name <span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Full name"
                          {...field}
                          disabled={!isNameEditable}
                          showSuccess={!!field.value && !form.formState.errors.individual_name}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 2. Date of Birth */}
                <FormField
                  control={form.control}
                  name="date_of_birth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">Date of Birth<span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          className="h-9"
                          showSuccess={!!field.value && !form.formState.errors.date_of_birth}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 3. Gender */}
                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gender <span className="text-red-500">*</span></FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger className="h-9" showSuccess={!!field.value && !form.formState.errors.gender}>
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="m">Male</SelectItem>
                          <SelectItem value="f">Female</SelectItem>
                          <SelectItem value="o">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 4. Phone Number */}
                <FormField
                  control={form.control}
                  name="phone_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number <span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input
                          placeholder="10-digit mobile number"
                          {...field}
                          maxLength={10}
                          inputMode="numeric"
                          className="h-9"
                          disabled
                          showSuccess={!!field.value && !form.formState.errors.phone_number}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 5. Individual PAN */}
                <FormField
                  control={form.control}
                  name="individual_pan_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>PAN <span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input
                          placeholder="10-digit PAN"
                          {...field}
                          maxLength={10}
                          className="uppercase h-9"
                          showSuccess={!!field.value && !form.formState.errors.individual_pan_number}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 6. Email */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          {...field}
                          value={field.value ?? ""}
                          className="h-9"
                          showSuccess={!!field.value && !form.formState.errors.email}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Aadhaar–PAN Linkage (read-only, from enrichment) */}
                {aadhaarLinked !== null && (
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 space-y-1">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Aadhaar–PAN Linkage
                    </p>
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          aadhaarLinked
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {aadhaarLinked ? (
                          <>
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            Linked
                          </>
                        ) : (
                          <>
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Not Linked
                          </>
                        )}
                      </span>
                      {maskedAadhaar && (
                        <span className="text-sm text-gray-600 font-mono tracking-wider">
                          {maskedAadhaar}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <fieldset className="border border-gray-300 rounded p-4 space-y-4">
                  <legend className="font-bold">Communication Address</legend>
                  <CompositePersonalCommunicationAddress
                    form={form}
                    prefix="communication"
                    requiredFlags={[true, true, true, false, true]}
                  />
                </fieldset>
                <fieldset className="border border-gray-300 rounded p-4 space-y-4">
                  <legend className="font-bold">Residential Address</legend>
                  <label className="flex items-center gap-2 text-sm pt-2">
                    <input
                      type="checkbox"
                      checked={sameAsComm}
                      onChange={(e) => {
                        setSameAsComm(e.target.checked);
                        if (e.target.checked) {
                          form.setValue("residential_address_line1", form.watch("communication_address_line1"));
                          form.setValue("residential_address_line2", form.watch("communication_address_line2"));
                          form.setValue("residential_address_line3", form.watch("communication_address_line3"));
                          form.setValue("residential_address_landmark", form.watch("communication_address_landmark"));
                          form.setValue("residential_address_pincode", form.watch("communication_address_pincode"));
                          form.trigger([
                            "residential_address_line1",
                            "residential_address_line2",
                            "residential_address_line3",
                            "residential_address_landmark",
                            "residential_address_pincode",
                          ]);
                        }
                      }}
                    />
                    Same as Communication Address
                  </label>

                  <CompositePersonalResidentialAddress
                    form={form}
                    prefix="residential"
                    requiredFlags={[true, true, true, false, true]}
                    disabled={sameAsComm}
                  />
                </fieldset>

                {/* 8. Consents */}
                <fieldset className="space-y-2 pt-4">
                  <legend className="font-semibold">Consents</legend>
                  <FormField
                    control={form.control}
                    name="declares_hhi_above_3_lakh"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2">
                        <input
                          id="declares_hhi_above_3_lakh_check"
                          type="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                        />
                        <FormLabel htmlFor="declares_hhi_above_3_lakh_check" className="text-sm">
                          I declare my household income exceeds ₹3 lakhs per annum
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                </fieldset>
              </fieldset>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCallbackOpen(true)}
                  className="w-full sm:w-auto h-9"
                >
                  Request Callback
                </Button>
                <Button
                  type="submit"
                  className="w-full sm:w-auto sm:ml-auto bg-intellend-green hover:bg-intellend-green/90 h-9"
                  disabled={loading || !form.formState.isValid}
                >
                  Next
                </Button>
              </div>
            </form>
          </Form>

        </div>
      </main>
      {/* Loader overlays page */}


      <CallbackHelp isOpen={isCallbackOpen} setIsOpen={setIsCallbackOpen} />

    </div>
  );
};

export default PersonalInfo;
